import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CheckCircle, Target, Shield, Award, Users, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = { Shield, Target, Award, Clock, Users };

const defaultValues = [
  { title: "Integrity", description: "We conduct business with honesty and transparency.", icon: "Shield" },
  { title: "Precision", description: "Attention to detail ensures exceptional results.", icon: "Target" },
  { title: "Quality", description: "We never compromise on materials or workmanship.", icon: "Award" },
  { title: "Reliability", description: "Count on us to deliver on time and within budget.", icon: "Clock" },
  { title: "Professionalism", description: "Our team maintains the highest standards.", icon: "Users" },
];

const defaultMilestones = [
  { year: "2009", event: "Astute Builders LLC founded in New Jersey" },
  { year: "2012", event: "Expanded services to commercial construction" },
  { year: "2015", event: "Opened operations in New York market" },
  { year: "2018", event: "Reached 100+ completed projects milestone" },
  { year: "2021", event: "Launched interior build-out division" },
  { year: "2024", event: "Celebrating 15 years of excellence" },
];

const defaultContent = {
  hero: {
    subtitle: "About Us",
    title: "Building Excellence Since 2009",
    description: "A legacy of precision, integrity, and uncompromising quality in construction services across New Jersey.",
    image_url: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920",
  },
  story: {
    subtitle: "Our Story",
    title: "From Vision to Reality",
    paragraphs: [
      "Founded in 2009, Astute Builders LLC began with a simple mission: to deliver construction services of the highest quality while maintaining unwavering integrity in every project we undertake.",
      "Over the years, we've grown from a small local contractor to a full-service general contracting firm serving both residential and commercial clients throughout New Jersey.",
      "Our team of experienced professionals brings together diverse expertise in construction, project management, and design to deliver exceptional results.",
    ],
    image_url: "https://images.unsplash.com/photo-1632862378103-8248dccb7e3d?w=800",
  },
  mission: {
    subtitle: "Our Mission",
    title: "Precision. Integrity. Built to Last.",
    description: "To deliver exceptional construction services through meticulous attention to detail, transparent communication, and an unwavering commitment to quality.",
  },
  why_choose_us: {
    subtitle: "Why Choose Us",
    title: "The Astute Builders Difference",
    features: [
      "Licensed, bonded, and fully insured",
      "Transparent pricing with no hidden costs",
      "Dedicated project management team",
      "Quality materials and skilled craftsmen",
      "Clear communication throughout the project",
      "On-time delivery guarantee",
      "Post-completion support and warranty",
    ],
    image_url: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800",
  },
};

export default function AboutPage() {
  const [content, setContent] = useState(defaultContent);
  const [values, setValues] = useState(defaultValues);
  const [milestones, setMilestones] = useState(defaultMilestones);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/site-content/about`);
        if (response.data?.content) {
          setContent(prev => ({ ...prev, ...response.data.content }));
          if (response.data.content.values) {
            setValues(response.data.content.values);
          }
          if (response.data.content.milestones) {
            setMilestones(response.data.content.milestones);
          }
        }
      } catch (error) {
        console.error("Error fetching about content:", error);
      }
    };
    fetchContent();
  }, []);

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-32 bg-[#0A0A0A] overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 opacity-20">
          <img
            src={content.hero?.image_url || defaultContent.hero.image_url}
            alt="Construction"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
            {content.hero?.subtitle || defaultContent.hero.subtitle}
          </span>
          <h1 className="font-['Oswald'] text-5xl md:text-6xl font-bold text-white mb-6">
            {content.hero?.title || defaultContent.hero.title}
          </h1>
          <div className="section-divider" />
          <p className="text-[#A1A1AA] text-xl max-w-2xl mt-6">
            {content.hero?.description || defaultContent.hero.description}
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-24 bg-[#121212]" data-testid="company-story">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img
                src={content.story?.image_url || defaultContent.story.image_url}
                alt="Team Working"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 w-48 h-48 border-4 border-[#7ED4C6]" />
            </div>
            <div>
              <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
                {content.story?.subtitle || defaultContent.story.subtitle}
              </span>
              <h2 className="font-['Oswald'] text-4xl font-bold text-white mb-6">
                {content.story?.title || defaultContent.story.title}
              </h2>
              <div className="section-divider" />
              <div className="space-y-4 text-[#A1A1AA] leading-relaxed mt-6">
                {(content.story?.paragraphs || defaultContent.story.paragraphs).map((para, index) => (
                  <p key={index}>{para}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="mission-statement">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
            {content.mission?.subtitle || defaultContent.mission.subtitle}
          </span>
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mb-8">
            {content.mission?.title || defaultContent.mission.title}
          </h2>
          <div className="section-divider mx-auto" />
          <p className="text-[#A1A1AA] text-xl leading-relaxed mt-8">
            {content.mission?.description || defaultContent.mission.description}
          </p>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-[#121212]" data-testid="core-values">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
              What Drives Us
            </span>
            <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mb-4">
              Our Core Values
            </h2>
            <div className="section-divider mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => {
              const IconComponent = iconMap[value.icon] || Shield;
              return (
                <div
                  key={index}
                  className="value-card"
                  data-testid={`value-card-${index}`}
                >
                  <div className="text-[#7ED4C6] mb-4">
                    <IconComponent size={40} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-['Oswald'] text-xl font-semibold text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-[#A1A1AA] leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="timeline">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
              Our Journey
            </span>
            <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mb-4">
              Company Milestones
            </h2>
            <div className="section-divider mx-auto" />
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-[#2A2A2A]" />

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className="relative flex items-start gap-8 pl-16">
                  <div className="absolute left-6 w-5 h-5 bg-[#7ED4C6] border-4 border-[#0A0A0A] transform -translate-x-1/2" />
                  <div>
                    <span className="text-[#7ED4C6] font-['Oswald'] text-2xl font-bold">
                      {milestone.year}
                    </span>
                    <p className="text-white text-lg mt-1">{milestone.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-[#121212]" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
                {content.why_choose_us?.subtitle || defaultContent.why_choose_us.subtitle}
              </span>
              <h2 className="font-['Oswald'] text-4xl font-bold text-white mb-6">
                {content.why_choose_us?.title || defaultContent.why_choose_us.title}
              </h2>
              <div className="section-divider" />
              <ul className="space-y-4 mt-8">
                {(content.why_choose_us?.features || defaultContent.why_choose_us.features).map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-white">
                    <CheckCircle size={20} className="text-[#7ED4C6] mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn-primary mt-8 inline-flex items-center gap-2" data-testid="about-contact-btn">
                Start Your Project
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="relative">
              <img
                src={content.why_choose_us?.image_url || defaultContent.why_choose_us.image_url}
                alt="Construction Excellence"
                className="w-full aspect-square object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#7ED4C6]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#0A0A0A] mb-6">
            Let's Build Something Great Together
          </h2>
          <p className="text-[#0A0A0A]/80 text-lg mb-8">
            Ready to start your next construction project? Our team is here to help you
            every step of the way.
          </p>
          <Link
            to="/contact"
            className="bg-[#0A0A0A] text-white font-['Oswald'] font-semibold uppercase tracking-wider px-8 py-4 hover:bg-[#121212] transition-colors inline-flex items-center justify-center gap-2"
          >
            Contact Us Today
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
